let imageUidSeed = 0;

function nextImageUid(prefix) {
  imageUidSeed += 1;
  return `${prefix || 'image'}-${Date.now()}-${imageUidSeed}`;
}

function defineUploadMeta(target, key, value) {
  if (!target || Object.prototype.hasOwnProperty.call(target, key)) return;
  Object.defineProperty(target, key, {
    configurable: true,
    enumerable: false,
    writable: true,
    value,
  });
}

export function ensureImageUid(image, prefix) {
  if (!image || typeof image !== 'object') return image;
  if (!image.uid) {
    Object.defineProperty(image, 'uid', {
      configurable: true,
      enumerable: false,
      writable: true,
      value: nextImageUid(prefix),
    });
  }
  return image;
}

export function normalizeImageList(list, prefix) {
  return (Array.isArray(list) ? list : []).map((item) => {
    const image = typeof item === 'string' ? { url: item } : item;
    return ensureImageUid(image, prefix);
  });
}

export function appendImageInSelectionOrder(list, file, image) {
  const source = Array.isArray(list) ? list : [];
  const next = normalizeImageList(source).slice();
  const target = ensureImageUid(image || {}, 'upload');
  if (file && file.uid) {
    target.uid = file.uid;
    defineUploadMeta(target, '__uploadOrder', Number(file.uid) || Date.now());
  }

  const existedIndex = next.findIndex((item) => item.uid === target.uid);
  if (existedIndex >= 0) {
    next.splice(existedIndex, 1, target);
  } else {
    next.push(target);
  }

  next.sort((left, right) => {
    const leftOrder = left.__uploadOrder;
    const rightOrder = right.__uploadOrder;
    if (leftOrder == null && rightOrder == null) return 0;
    if (leftOrder == null) return -1;
    if (rightOrder == null) return 1;
    return leftOrder - rightOrder;
  });

  // Element UI watches file-list by reference and replaces its internal queue
  // when the reference changes, so keep the caller's array identity stable.
  source.splice(0, source.length, ...next);
  return source;
}

export function removeImageByUploadFile(list, file) {
  const source = Array.isArray(list) ? list : [];
  const next = normalizeImageList(source).filter((item) => {
    if (file && file.uid && item.uid) return item.uid !== file.uid;
    return !file || item.url !== file.url;
  });
  source.splice(0, source.length, ...next);
  return source;
}

function currentSession(file) {
  if (!file) return undefined;
  if (file.__imageUploadSession != null) return file.__imageUploadSession;
  return file.raw && file.raw.__imageUploadSession;
}

export const imageUploadMixin = {
  data() {
    return {
      imageUploadScopes: {},
    };
  },
  methods: {
    getStableImageFileList(scope, list) {
      if (!this._imageUploadFileLists) this._imageUploadFileLists = Object.create(null);
      const source = Array.isArray(list) ? list : null;
      const cached = this._imageUploadFileLists[scope];
      const state = this.imageUploadScopes[scope];
      const active = state && (state.pending > 0 || state.failed > 0);
      const items = source || [];
      const changed = cached && (cached.files.length !== items.length || cached.files.some((item, index) => {
        const current = items[index];
        return item !== current && !(typeof current === 'string' && item.url === current);
      }));
      if (!cached || (!active && (cached.source !== source || changed))) {
        // Element UI rebuilds its upload queue even for in-place Vue array mutations.
        this._imageUploadFileLists[scope] = {
          source,
          files: normalizeImageList(items, scope).slice(),
        };
      }
      return this._imageUploadFileLists[scope].files;
    },
    getImageUploadScope(scope) {
      if (!this.imageUploadScopes[scope]) {
        this.$set(this.imageUploadScopes, scope, {
          session: 0,
          pending: 0,
          failed: 0,
        });
      }
      return this.imageUploadScopes[scope];
    },
    trackImageUpload(scope, file, fileList) {
      const state = this.getImageUploadScope(scope);
      if (file && file.status === 'ready') {
        file.__imageUploadSession = state.session;
        if (file.raw) file.raw.__imageUploadSession = state.session;
      }
      if (file && currentSession(file) != null && currentSession(file) !== state.session) return false;

      const sourceFiles = Array.isArray(fileList) ? fileList : [];
      const removed = file && (file.__imageUploadRemoved || (file.raw && file.raw.__imageUploadRemoved));
      if (file && file.status === 'fail' && !removed && !sourceFiles.some((item) => item === file || (item.uid && item.uid === file.uid))) {
        // Element UI removes HTTP failures before on-error, so restore the item
        // until the user explicitly removes it or retries.
        sourceFiles.push(file);
      }

      const activeFiles = sourceFiles.filter((item) => {
        const session = currentSession(item);
        return session == null || session === state.session;
      });
      state.pending = activeFiles.filter((item) => item.status === 'ready' || item.status === 'uploading').length;
      state.failed = activeFiles.filter((item) => item.status === 'fail').length;
      this.$set(this.imageUploadScopes, scope, Object.assign({}, state));
      return true;
    },
    isCurrentImageUpload(scope, file) {
      if (file && (file.__imageUploadRemoved || (file.raw && file.raw.__imageUploadRemoved))) return false;
      const state = this.getImageUploadScope(scope);
      const session = currentSession(file);
      return session == null || session === state.session;
    },
    resetImageUploadScope(scope) {
      const state = this.getImageUploadScope(scope);
      if (this._imageUploadFileLists) delete this._imageUploadFileLists[scope];
      this.$set(this.imageUploadScopes, scope, {
        session: state.session + 1,
        pending: 0,
        failed: 0,
      });
    },
    removeTrackedImage(scope, file, fileList) {
      if (file) {
        file.__imageUploadRemoved = true;
        if (file.raw) file.raw.__imageUploadRemoved = true;
      }
      this.trackImageUpload(scope, file, fileList);
    },
    guardImageUploads(scopes) {
      const names = scopes && scopes.length ? scopes : Object.keys(this.imageUploadScopes);
      const states = names.map((scope) => this.getImageUploadScope(scope));
      if (states.some((state) => state.pending > 0)) {
        this.$message.warning('图片正在上传，请稍后');
        return false;
      }
      if (states.some((state) => state.failed > 0)) {
        this.$message.warning('有图片上传失败，请移除后重新选择');
        return false;
      }
      return true;
    },
    handleImageUploadExceed(max, files, fileList) {
      const current = (fileList || []).length;
      const remaining = Math.max(0, max - current);
      this.$message.warning(`最多上传${max}张，当前还可选择${remaining}张，本次选择的${files.length}张未上传`);
    },
  },
};
